import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { extractError } from "../lib/formatError";

export default function AdminGameConfig() {
  const [cfg, setCfg] = useState(null);
  const [audit, setAudit] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/game-config");
      setCfg(data.config); setAudit(data.audit); setDraft(data.config); setError(null);
    } catch (e) { setError(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setShowConfirm(false); setBusy(true); setError(null);
    // Diff draft vs cfg — only send changed keys
    const patch = {};
    for (const k of Object.keys(draft)) {
      if (JSON.stringify(draft[k]) !== JSON.stringify(cfg[k])) patch[k] = draft[k];
    }
    if (Object.keys(patch).length === 0) { setBusy(false); toast.info("No changes"); return; }
    try {
      const { data } = await api.post("/admin/game-config", patch);
      setCfg(data.config); setDraft(data.config);
      await load();
      toast.success("Config saved");
    } catch (e) { setError(extractError(e)); } finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-[color:var(--muted)] text-sm">Loading…</div>;

  const dirty = JSON.stringify(cfg) !== JSON.stringify(draft);

  return (
    <div data-testid="admin-game-config">
      <Toaster theme="dark" position="top-center" />
      <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)] mb-2">Game config</div>
      <h1 className="font-display italic text-4xl text-[color:var(--ink)] mb-1">Live game rules</h1>
      <p className="text-sm text-[color:var(--muted)] mb-6 max-w-xl">
        Changes apply to <b>new bets only</b>. Rounds already in flight keep the values they were placed with.
      </p>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Payout & bet limits">
          <Num label="Payout multiplier (× stake)" value={draft.payout_multiplier} onChange={(v) => setDraft({ ...draft, payout_multiplier: Number(v) })} testid="cfg-payout" min="1.01" max="99.99" step="0.01" />
          <Num label="Minimum bet ($)" value={draft.min_bet} onChange={(v) => setDraft({ ...draft, min_bet: Number(v) })} testid="cfg-min-bet" />
          <Num label="Max bet · demo ($)" value={draft.max_bet_demo} onChange={(v) => setDraft({ ...draft, max_bet_demo: Number(v) })} testid="cfg-max-demo" />
          <Num label="Max bet · real ($)" value={draft.max_bet_real} onChange={(v) => setDraft({ ...draft, max_bet_real: Number(v) })} testid="cfg-max-real" />
          <Num label="Round timeout (s)" value={draft.round_timeout_seconds} onChange={(v) => setDraft({ ...draft, round_timeout_seconds: Number(v) })} step="1" testid="cfg-timeout" />
        </Section>

        <Section title="Wallet + KYC">
          <Num label="Min deposit ($)" value={draft.min_deposit_usd} onChange={(v) => setDraft({ ...draft, min_deposit_usd: Number(v) })} testid="cfg-min-dep" />
          <Num label="Min withdraw ($)" value={draft.min_withdraw_usd} onChange={(v) => setDraft({ ...draft, min_withdraw_usd: Number(v) })} testid="cfg-min-wd" />
          <Num label="KYC threshold ($)" value={draft.kyc_threshold_usd} onChange={(v) => setDraft({ ...draft, kyc_threshold_usd: Number(v) })} testid="cfg-kyc-thr" />
        </Section>

        <Section title="Colors">
          <div className="grid grid-cols-3 gap-2">
            {draft.colors.map((c, i) => (
              <div key={`color-slot-${i}-${c}`}>
                <input
                  value={c}
                  onChange={(e) => {
                    const cs = [...draft.colors]; cs[i] = e.target.value;
                    setDraft({ ...draft, colors: cs });
                  }}
                  className="bet-input text-xs uppercase"
                  data-testid={`cfg-color-${i}`}
                />
                <input
                  type="color"
                  value={draft.color_display_hex[c] || "#666666"}
                  onChange={(e) => {
                    const hex = { ...draft.color_display_hex, [c]: e.target.value };
                    setDraft({ ...draft, color_display_hex: hex });
                  }}
                  className="mt-2 w-full h-10 rounded-none bg-transparent border border-[color:var(--line)]"
                  data-testid={`cfg-hex-${i}`}
                />
                <div className="text-[10px] font-mono text-center mt-1" style={{ color: draft.color_display_hex[c] || "var(--muted)" }}>
                  {draft.color_display_hex[c] || "—"}
                </div>
              </div>
            ))}
          </div>
          <div className="text-[9px] tracking-widest uppercase text-[color:var(--muted)] mt-3">
            Exactly 3 distinct, non-empty color names required. Past rounds keep their snapshotted colors.
          </div>
        </Section>

        <Section title="Preview">
          <div className="flex items-center justify-center gap-3 py-6 border" style={{ borderColor: "var(--line)", background: "var(--shell-2)" }}>
            {draft.colors.map((c, i) => (
              <div key={`preview-slot-${i}-${c}`} className="text-center">
                <div className="w-16 h-16 border-2" style={{ background: draft.color_display_hex[c] || "#666", borderColor: "var(--line)" }} />
                <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mt-1">{c}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-3 text-[color:var(--gold)] font-mono text-2xl">{draft.payout_multiplier.toFixed(2)}×</div>
          <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] text-center">winning payout</div>
        </Section>
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={() => setDraft(cfg)} disabled={!dirty || busy} className="px-4 py-2 border border-[color:var(--line)] text-[10px] tracking-widest uppercase text-[color:var(--muted)] disabled:opacity-40" data-testid="cfg-reset">Reset</button>
        <button onClick={() => setShowConfirm(true)} disabled={!dirty || busy} className="spin-btn px-8" data-testid="cfg-save">{busy ? "Saving…" : "Save"}</button>
      </div>

      <div className="mt-10">
        <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-3">Change log (last 20)</div>
        {audit.length === 0 ? <div className="text-xs text-[color:var(--muted)] italic">No changes yet.</div>
          : (
            <div className="space-y-2 text-[11px] font-mono">
              {audit.map((a, i) => (
                <div key={`${a.at}-${i}`} className="border p-2" style={{ borderColor: "var(--line)" }} data-testid={`audit-${i}`}>
                  <div className="text-[color:var(--muted)] text-[10px]">{new Date(a.at).toLocaleString()} · admin {a.admin_id.slice(0, 8)}</div>
                  <div className="text-[color:var(--ink)]">{JSON.stringify(a.after)}</div>
                </div>
              ))}
            </div>
          )
        }
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(6,4,3,0.85)", backdropFilter: "blur(4px)" }} data-testid="cfg-confirm-modal">
          <div className="w-full max-w-md border p-6" style={{ borderColor: "var(--red)", background: "var(--card)" }}>
            <div className="flex items-center gap-3 mb-3 text-[color:var(--red)]">
              <AlertTriangle className="w-5 h-5" />
              <div className="font-display italic text-xl">Affect live gameplay</div>
            </div>
            <p className="text-xs text-[color:var(--muted)] mb-4">
              This will change the game rules for all <b>new bets</b>. Existing pending rounds keep their snapshotted values.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setShowConfirm(false)} className="flex-1 py-2 border border-[color:var(--line)] text-[10px] tracking-widest uppercase text-[color:var(--muted)]">Cancel</button>
              <button onClick={save} className="flex-1 py-2 border border-[color:var(--red)] text-[10px] tracking-widest uppercase text-[color:var(--red)]" data-testid="cfg-confirm-save">Confirm change</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border p-4" style={{ borderColor: "var(--line)", background: "var(--card)" }}>
      <div className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
function Num({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block col-span-2 sm:col-span-1">
      <div className="text-[9px] tracking-widest uppercase text-[color:var(--muted)] mb-1">{label}</div>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="bet-input text-xs font-mono" data-testid={testid} step="0.01" {...rest} />
    </label>
  );
}
